/**
* @description: 考勤异常管理
*/
<template>
    <div class="finance_box" ref="myElementRef">
        <div ref="searchBox">
            <hc-search :data="searchData" @serch="getSearch" />
        </div>
        <!-- 表格 -->
        <div class="table_box">
            <hc-table :loading="state.loading" :tableData="state.tableData" :column="state.tableHeader" :offsetHeight="state.offsetHeight" :maxPage="maxPage"
                @pageChange="pageChange" @limitChange="limitChange">
                <template #makeup_date="{ row }">
                    {{ formatDisplayDate(row.makeup_date) || '-' }}
                </template>
                <template #makeup_time="{ row }">
                    {{ formatDisplayTime(row.makeup_time) || '-' }}
                </template>
                <template #makeup_type="{ row }">
                    {{ listToMap(typeOptions, {})[row.makeup_type] || row.makeup_type || '-' }}
                </template>
                <template #status="{ row }">
                    <el-tag :type="statusTypeMap[row.status]" size="small">{{ listToMap(statusOptions, {})[row.status] }}</el-tag>
                </template>
                <template #operation="{ row }">
                    <el-button 
                        v-if="canAudit(row)"
                        class="opBtn" 
                        size="small" 
                        type="primary" 
                        link
                        @click="openAudit(row)"
                    >{{ t('attendance.audit') }}</el-button>
                </template>
            </hc-table>
        </div>
        <!-- 审核弹窗 -->
        <AnomalyAudit ref="audit" :title="state.title" :row="state.row" @refresh="getSearch(searchData)" />
    </div>
</template>

<script setup>
import hcSearch from "@/components/hc-search/index.vue";
import hcTable from "@/components/hc-table/index.vue";
import { onMounted, reactive, ref, nextTick, onUnmounted, toRaw, computed } from "vue";
import AnomalyAudit from "./components/anomalyAudit.vue";
import { useAttendancePageI18n, t } from '@/composables/useAttendancePageI18n.js';

import { pageListApi } from "@/api/attendance/anomaly";
import { listToMap } from "@/utils/utils";
import { formatDisplayDate, formatDisplayTime } from "@/utils/dateTime.js";

const myElementRef = ref();
const searchBox = ref();
const audit = ref();

let page = ref(1);
let limit = ref(20);
let maxPage = ref(0);

const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
const roleId = userInfo?.users?.roleId || userInfo?.users?.role_id || userInfo?.user?.users?.roleId || userInfo?.user?.users?.role_id || 0;
const isCaptain = computed(() => {
    const id = Number(roleId);
    return id === 2;
});
const isAdmin = computed(() => {
    const id = Number(roleId);
    return id === 99 || id >= 3;
});

const buildStatusOptions = () => ([
    { label: t('attendance.pendingAudit'), value: 0 },
    { label: t('attendance.captainApproved'), value: 1 },
    { label: t('attendance.captainRejected'), value: 2 },
    { label: t('attendance.adminApproved'), value: 3 },
    { label: t('attendance.adminRejected'), value: 4 },
    { label: t('common.all'), value: 99 },
]);

const statusOptions = reactive(buildStatusOptions());
const statusTypeMap = {
    0: 'warning',
    1: 'success',
    2: 'danger',
    3: 'success',
    4: 'danger',
}

const buildTypeOptions = () => ([
    { label: t('attendance.onMachine'), value: 'on_machine' },
    { label: t('attendance.offMachine'), value: 'off_machine' },
    { label: t('attendance.ot'), value: 'ot' },
    { label: t('attendance.zhongzhi'), value: 'zhongzhi' },
]);

const typeOptions = reactive(buildTypeOptions());

const searchData = reactive([
    { label: "", type: "input", value: "", field: 'keywords' },
    {
        label: "",
        type: "select",
        value: 99,
        defaultValue: 99,
        field: 'status',
        children: statusOptions,
        clearable: false
    },
    { label: "", type: "daterange", value: [], field: 'time', seditem: 'stime', enditem: 'etime' },
]);

const buildColumns = () => ([
    { prop: "id", show: false, label: "ID" },
    { show: true, type: "index", label: t('table.index'), width: 80 },
    { prop: "name", show: true, label: t('field.realName') },
    { prop: "makeup_date", show: true, label: t('attendance.makeupDate') },
    { prop: "makeup_time", show: true, label: t('attendance.makeupTime') },
    { prop: "makeup_type", show: true, label: t('attendance.makeupType') },
    { prop: "equipment_title", show: true, label: t('field.deviceName') },
    { prop: "project_location_name", show: true, label: t('attendance.projectSite') },
    { prop: "overtime_hours", show: true, label: t('attendance.otHours') },
    { prop: "reason", show: true, label: t('attendance.anomalyReason') },
    { prop: "status", show: true, label: t('field.status') },
    { prop: "created_at", show: true, label: t('attendance.createdAt') },
    { prop: "operation", show: true, label: t('table.operation') },
]);

const applyLabels = () => {
    searchData[0].label = t('field.keywords');
    searchData[1].label = t('field.status');
    searchData[2].label = t('common.startTime');
    Object.assign(statusOptions, buildStatusOptions());
    Object.assign(typeOptions, buildTypeOptions());
    state.column = buildColumns();
    tableHeaderFn();
};

useAttendancePageI18n(applyLabels);

const state = reactive({
	loading: false,
    title: "",
    column: buildColumns(),
    offsetHeight: 0,
    tableHeader: [],
    tableData: [],
    row: {}
});

const init = () => {
    getSearch(searchData)
};

const offHeighths = () => {
    if (myElementRef.value) {
        state.offsetHeight =
            myElementRef.value?.offsetHeight - 90 - searchBox.value?.offsetHeight;
    }
};

const pageChange = (currentPage) => {
    page.value = currentPage;
    getSearch(searchData)
}

const limitChange = (currentLimit) => {
    limit.value = currentLimit;
    getSearch(searchData)
}

const canAudit = (row) => {
    const status = Number(row.status);
    const currentRoleId = Number(roleId);
    
    if (currentRoleId === 2 && status === 0) {
        return true;
    }
    
    if ((currentRoleId === 99 || currentRoleId >= 3) && (status === 0 || status === 1)) {
        return true;
    }
    
    return false;
};

const openAudit = (row) => {
    audit.value.open(row)
    state.row = row
};

onMounted(() => {
    try {
        applyLabels();
        nextTick(() => {
            offHeighths();
        });
        window.onresize = () => {
            nextTick(() => {
                offHeighths();
            });
        };
        tableHeaderFn();
        init();
    } catch { }
});

onUnmounted(() => {
    window.onresize = null;
});

const formatDate = (date) => formatDisplayDate(date) || '-';

const formatTime = (time) => formatDisplayTime(time) || '-';

const getSearch = (params) => {
    params.forEach(i => i.value = i.value || '')
    const form = listToMap(toRaw(params), { value: 'value', key: 'field' })
    const requestParams = {
        page: page.value,
        limit: limit.value,
        ...form
    }
    state.loading = true
		pageListApi(JSON.stringify(requestParams)).then(res => {
        maxPage.value = res?.maxPage ?? 0;
        state.tableData = res?.data?.map(item => {
            return {
                ...item,
                overtime_hours: item.overtime_hours ? item.overtime_hours + t('detail.unitHour') : '-',
                status: Number(item.status) || 0
            }
        }) ?? []
    }).finally(() => {
		state.loading = false
	})
};

const tableHeaderFn = () => {
    state.tableHeader = state.column.filter((v) => {
        if (v.show) {
            return v.show == true;
        } else {
            v.show = false;
        }
    });
};
</script>

<style lang="less" scoped>
.finance_box {
    flex: 1;
    overflow: auto;
    background-color: #fff;
    border-radius: 6px;
    padding: 10px;
    margin-right: 10px;
}

.btn_box {
    height: 40px;
    display: flex;
}

.table_box {
    width: 100%;
}

/deep/.el-button-group .el-button--primary:first-child {
    background-color: #1e6aed;
    border: none;
}

.serBtn {
    margin-left: 10px;
    background-color: #f0f0f0;
    color: #000;
    border: none;
}

.serBtn:hover {
    background-color: #f0f0f0;
    color: #000;
}

.opBtn {
    margin: 0 10px 0 0;
}

.opBtn:hover {
    color: #e6a755;
}
</style>
